import { useSubscription } from '@apollo/client';
import { Checkbox, Paper, Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import makeStyles from '@mui/styles/makeStyles';
import AlbumIcon from '@mui/icons-material/Album';
import BallotIcon from '@mui/icons-material/Ballot';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import QueueIcon from '@mui/icons-material/Queue';
import RefreshIcon from '@mui/icons-material/Refresh';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValueLoadable } from 'recoil';
import {
  useGetUserDirectorySubscriptionDataByUserIdMutation,
  useGetUserDirectorySubscriptionDataQuery,
} from '../../api/apigQuery';
import { getOnCreateEventQuery } from '../../api/apollo-appsync/graphql/subscriptions';
import { MaterialAutocomplete } from '../../common/formik-material-ui';
import { KIXIE_COLORS } from '../../constants';
import { endingDate } from '../../recoil/atoms/endingDate';
import { startingDate } from '../../recoil/atoms/startingDate';
import { user } from '../../recoil/atoms/user';
import { agentSummary } from '../../recoil/selectors/agentSummary';
import { methodNameEnum } from '../../recoil/selectors/makePHPRequest';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../services/rest/makeAPIRequest';
import { PowerlistModal } from '../components/modals/PowerlistModal';
import { useSnackbarWithStyles } from '../components/notifications/SnackbarProviderWithStyles';
import { QueueEditDialog } from '../modules/Manage/pages/queue-edit-dialog/QueueEditDialog';
import { DataTable } from '../modules/Manage/pages/queues-table/DataTable';

const getDurationInSeconds = (duration) => {
  const durationArr = duration.split(':');
  return (
    Number(durationArr[0] * 3600) +
    Number(durationArr[1] * 60) +
    Number(durationArr[2])
  );
};

const useStyles = makeStyles((theme) => ({
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  accordionContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    padding: '24px 0px',
  },
  powerlistContainer: {
    width: '356px',
    display: 'flex',
    justifyContent: 'flex-start',
  },
  selectedRow: {
    backgroundColor:
      theme.palette.mode === 'light'
        ? 'rgb(240,240,240, .8)'
        : 'rgb(135,135,135, .7)',
  },
  clearButton: {
    marginLeft: '16px',
    '&:hover': {
      color:
        theme.palette.mode === 'light'
          ? 'rgb(170,170,170, .9)'
          : 'rgb(135,135,135, .7)',
    },
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  powerlistListContainer: {
    width: '164px',
  },
  queuesListContainer: {
    width: '200px',
    marginRight: '36px',
  },
  started: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#c9f7f5',
    borderRadius: '4px',
  },
  onPause: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgb(180, 212, 249)',
    borderRadius: '4px',
  },
  ended: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor:
      theme.palette.mode === 'light'
        ? 'rgb(155,155,155, .7)'
        : 'rgb(135,135,135, .7)',
    borderRadius: '4px',
  },
  startedIcon: {
    height: '14px',
    width: '14px',
    color: '#1bc5bd',
    margin: '2px 6px',
  },
  onPauseIcon: {
    height: '14px',
    width: '14px',
    color: '#0098C7',
    margin: '2px 6px',
  },
  endedIcon: {
    height: '14px',
    width: '14px',
    color:
      theme.palette.mode === 'light'
        ? 'rgb(55,55,55, .7)'
        : 'rgb(245,245,245, .7)',
    margin: '2px 6px',
  },
  startedText: {
    fontSize: '11px',
    margin: '2px 12px',
    color:
      theme.palette.mode === 'light'
        ? 'rgb(55,55,55, .7)'
        : 'rgb(105,105,105, .7)',
    fontWeight: 600,
  },
  onPauseText: {
    fontSize: '11px',
    margin: '2px 12px',
    color:
      theme.palette.mode === 'light'
        ? 'rgb(55,55,55, .7)'
        : 'rgb(105,105,105, .7)',
    fontWeight: 600,
  },
  endedText: {
    fontSize: '11px',
    margin: '2px 12px',
    color:
      theme.palette.mode === 'light'
        ? 'rgb(55,55,55, .7)'
        : 'rgb(245,245,245, .7)',
    fontWeight: 600,
  },
  subscriptionCheckbox: {
    padding: '0px',
    color: '#0098C7',
    '& svg': {
      fontSize: '14px',
    },
  },
  powerlistContentContainer: {
    marginTop: '12px',
  },
  powerlistContentContainer2: {
    marginTop: '10px',
  },
  subscriptionText: {
    marginLeft: '4px',
    display: 'inline',
  },
  subscriptionLink: {
    marginLeft: '4px',
    color: '#0098C7',
    cursor: 'pointer',
    display: 'inline',
  },
  dropdownContainer: {
    display: 'flex',
    paddingTop: '12px',
  },
  accordionHeaderContainer: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  statusCell: {
    display: 'flex',
    justifyContent: 'center',
  },
  accordionHeader: {
    fontSize: '11px',
    fontWeight: 900,
    display: 'flex',
    alignItems: 'center',
  },
  accordionHeaderIcon: {
    width: '14px',
    height: '14px',
    marginRight: '4px',
  },
  powerlistSessionStatusContainer: {
    marginRight: '24px',
    marginLeft: '16px',
  },
  callsHandledContainer: {
    margin: '0px 2px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  callsMissedContainer: {
    margin: '0px 2px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  statsContainer: {
    width: '100%',
    maxWidth: '280px',
    minWidth: '200px',
  },
  queueCallsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  queueCallsBarContainer: {
    display: 'flex',
    marginTop: '4px',
  },
  queueCallsContainerAnswered: {
    height: '16px',
    backgroundColor: '#0098C7',
    borderTopLeftRadius: '8px',
    borderBottomLeftRadius: '8px',
  },
  queueCallsContainerMissed: {
    height: '16px',
    backgroundColor: 'rgb(175, 220, 235)',
    borderTopRightRadius: '8px',
    borderBottomRightRadius: '8px',
  },
  queueCallsContainerNone: {
    height: '16px',
    width: '100%',
    backgroundColor:
      theme.palette.mode === 'light'
        ? 'rgb(155,155,155, .7)'
        : 'rgb(135,135,135, .7)',
    borderRadius: '8px',
  },
  queueCallsBarDivider: {
    marginLeft: '2px',
  },
  queueContainer: {
    // width: '400px',
    display: 'flex',
    marginLeft: '60px',
  },
  verticalRuler: {
    borderLeft:
      theme.palette.mode === 'light'
        ? '1px solid rgb(55,55,55, .24)'
        : '1px solid rgb(155,155,155, .54)',
    height: 'auto',
  },
  field: {
    margin: 10,
    width: 250,
    '& .MuiFormLabel-root[data-shrink=false]': {
      lineHeight: 0.25,
    },
    '& .MuiInputBase-input': {
      padding: '14px 12px',
    },
    '& .MuiFormHelperText-root': {
      marginLeft: 0,
    },
    '& .MuiInputLabel-root[data-shrink=false]': {
      opacity: 0.5,
    },
  },
  expandButton: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color:
      theme.palette.mode === 'light'
        ? 'rgb(55,55,55, .4)'
        : 'rgb(255,255,255, .54)',
    border:
      theme.palette.mode === 'light'
        ? '1px solid rgb(55,55,55, .4)'
        : '1px solid rgb(255,255,255, .54)',
    borderRadius: '2px',
    height: '16px',
    width: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '14px',
  },
  dropdown: {
    width: '220px',
    marginLeft: '16px',
  },
  skeleton: {
    backgroundColor: theme?.palette?.mode === 'dark' ? 'grey' : 'lightgrey',
    height: '6em',
    width: '100%',
    margin: 10,
  },
  refreshIcon: {
    height: '16px',
    width: '16px',
  },
  refreshButtonContainer: {
    marginRight: '24px',
  },
  refreshButton: {
    padding: theme.spacing(0),
    width: '20px',
    height: '20px',
    backgroundColor:
      theme?.palette?.mode === 'dark' ? 'rgb(73,84,101)' : 'rgb(250,251,251)',
    '&:hover': {
      backgroundColor:
        theme?.palette?.mode === 'dark' ? 'rgb(73,84,101)' : 'rgb(250,251,251)',
    },
    border:
      theme?.palette?.mode === 'dark'
        ? 'solid 1px rgb(71,84,85)'
        : 'solid 1px rgb(194,192,195)',
    borderRadius: '4px',
  },
  refreshButtonDisabled: {
    padding: theme.spacing(0),
    width: '20px',
    height: '20px',
    backgroundColor:
      theme?.palette?.mode === 'dark' ? 'rgb(73,84,101)' : 'rgb(250,251,251)',
    '&:hover': {
      backgroundColor:
        theme?.palette?.mode === 'dark' ? 'rgb(73,84,101)' : 'rgb(250,251,251)',
    },
    border:
      theme?.palette?.mode === 'dark'
        ? 'solid 1px rgb(71,84,85)'
        : 'solid 1px rgb(194,192,195)',
    borderRadius: '4px',
  },
}));

const cellFieldTypeMap = {
  name: 'string',
  status: 'string',
  customerName: 'string',
  customerNumber: 'string',
  direction: 'string',
  tdurationread: 'duration',
  avgduration: 'duration',
};

export function CallBoard() {
  const classes = useStyles();
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();

  const [filters, setFilters] = useState([
    {
      teamid: 'all',
      teamname: 'All Teams',
    },
    {
      id: 'all',
      name: 'All PowerLists',
    },
    {
      queueid: 'all',
      queuename: 'All Queues',
    },
  ]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [refreshToggle, setRefreshToggle] = useState(false);
  const [directory, setDirectory] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [openArr, setOpenArr] = useState({});
  const [throttleArr, setThrottleArr] = useState({});
  const [editEntity, setEditEntity] = useState({
    type: null,
    id: null,
  });
  const storageUser = useRecoilValueLoadable(user);
  const agentSummaryLoadable = useRecoilValueLoadable(agentSummary);
  const startingLoadable = useRecoilValueLoadable(startingDate);
  const endingLoadable = useRecoilValueLoadable(endingDate);
  const [
    getUserDirectorySubscriptionDataByUserId,
    getUserDirectorySubscriptionDataByUserIdResult,
  ] = useGetUserDirectorySubscriptionDataByUserIdMutation();

  const resetFilters = () => {
    setFilters([
      {
        teamid: 'all',
        teamname: 'All Teams',
      },
      {
        id: 'all',
        name: 'All PowerLists',
      },
      {
        queueid: 'all',
        queuename: 'All Queues',
      },
    ]);
    setStatusFilter([]);
  };

  const headCells = [
    {
      field: 'actions',
      headerName: 'Expand',
      // dragTableWidth: '50%',
      width: 48,
      enableSorting: false,
      headerAlign: 'center',
      disableExport: true,
      renderCell: (params) => {
        const clickHandler = () =>
          setOpenArr({
            ...openArr,
            [params.userid]: !openArr[params.userid],
          });
        return (
          <Tooltip title={openArr[params.userid] ? 'Collapse' : 'Expand'}>
            <div onClick={clickHandler} className={classes.expandButton}>
              {openArr[params.userid] ? '-' : '+'}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: 'name',
      headerName: 'Name',
      type: 'string',
      width: 180,
    },
    {
      field: 'status',
      headerName: 'Status',
      cellClassName: classes.statusCell,
      type: 'string',
      // align: 'center',
      width: 64,
      renderCell: (params) => {
        if (params.status === 'offline') {
          return (
            <Tooltip title={'Offline'}>
              <AlbumIcon style={{ color: KIXIE_COLORS.loggedOut }} />
            </Tooltip>
          );
        } else if (params.status === 'online') {
          return (
            <Tooltip title={'Online'}>
              <AlbumIcon style={{ color: KIXIE_COLORS.available }} />
            </Tooltip>
          );
        } else if (params.status === 'oncall') {
          return (
            <Tooltip title={'On Call'}>
              <AlbumIcon style={{ color: KIXIE_COLORS.onCall }} />
            </Tooltip>
          );
        }
        return (
          <Tooltip title={'Do-Not-Disturb (DND)'}>
            <AlbumIcon style={{ color: KIXIE_COLORS.dnd }} />
          </Tooltip>
        );
      },
    },
    {
      field: 'currentDuration',
      headerName: 'Current Duration',
      width: 160,
    },
    {
      field: 'customerName',
      headerName: 'Customer Name',
      type: 'string',
      width: 150,
      renderCell: (params) => {
        return params?.crmlink ? (
          <a target="_blank" rel="noreferrer" href={params.crmlink}>
            {params.customerName}
          </a>
        ) : (
          <div>{params.customerName}</div>
        );
      },
    },
    {
      field: 'customerNumber',
      headerName: 'Customer Phone',
      type: 'string',
      width: 150,
      // dragTableWidth: '38%',
    },
    {
      field: 'direction',
      headerName: 'Direction',
      type: 'string',
      width: 100,
    },
    {
      field: 'allcalls',
      headerName: 'Calls',
      width: 120,
    },
    {
      field: 'allconnections',
      headerName: 'Connections',
      width: 150,
    },
    {
      field: 'tdurationread',
      headerName: 'Duration',
      type: 'string',
      width: 150,
    },
    {
      field: 'avgduration',
      headerName: 'Avg. Duration',
      type: 'string',
      width: 150,
    },
  ];

  const powerlistReportLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: `${endpointEnum.GET_BUSINESS_POWERLISTS}`,
      method: 'POST',
      payloadJsonString: JSON.stringify({
        call: 'v1/getBusinessPowerlists',
      }),
    })
  );

  const queuesLiveLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_QUEUES_LIVE_REPORTING,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_QUEUES_LIVE_REPORTING,
      }),
    })
  );

  const businessUsersLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_USERS,
      }),
    })
  );
  const businessTeamsLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_TEAMS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_TEAMS,
        apikey: storageUser.contents?.apikey,
        businessid: storageUser.contents?.businessid,
      }),
    })
  );

  const powerlists = useMemo(
    () =>
      powerlistReportLoadable.state === 'hasValue'
        ? powerlistReportLoadable?.contents?.response?.data?.powerlists ?? []
        : [],
    [powerlistReportLoadable]
  );

  const queues = useMemo(
    () =>
      queuesLiveLoadable.state === 'hasValue'
        ? queuesLiveLoadable?.contents?.response?.data?.result ?? []
        : [],
    [queuesLiveLoadable]
  );

  const BusinessUsers = useMemo(
    () =>
      businessUsersLoadable.state === 'hasValue'
        ? [
            ...(businessUsersLoadable.contents?.response?.data?.result ?? []),
          ].filter((e) => e.isactive === 1)
        : [],
    [businessUsersLoadable]
  );

  const dateRange = useMemo(() => {
    if (
      startingLoadable?.state === 'hasValue' &&
      endingLoadable?.state === 'hasValue'
    ) {
      return {
        startDate: startingLoadable?.contents?._i,
        endDate: endingLoadable?.contents?._i,
      };
    } else {
      return {
        startDate: null,
        endDate: null,
      };
    }
  }, [startingLoadable, endingLoadable]);

  const BusinessTeams = useMemo(
    () =>
      businessTeamsLoadable.state === 'hasValue'
        ? [
            ...(businessTeamsLoadable.contents?.response?.data?.result ?? []),
          ].sort((a, b) =>
            a.teamname.toLowerCase().localeCompare(b.teamname.toLowerCase())
          )
        : [],
    [businessTeamsLoadable]
  );

  const allOpen = useMemo(() => {
    let allUsersExpanded = true;
    Object.keys(openArr).forEach((key) => {
      if (openArr[key] === false) {
        allUsersExpanded = false;
      }
    });
    return allUsersExpanded;
  }, [openArr]);

  const toggleExpandAll = useCallback(() => {
    const openArrCopy = {
      ...openArr,
    };
    Object.keys(openArrCopy).forEach((key) => {
      openArrCopy[key] = !allOpen;
    });
    setOpenArr(openArrCopy);
  }, [allOpen]);

  const BusinessTeamsOptions = useMemo(() => {
    return [
      {
        teamid: 'all',
        teamname: 'All Teams',
      },
    ].concat(
      (BusinessTeams ?? []).map((team) => ({
        teamname: team.teamname ?? '',
        teamid: team.teamid,
      }))
    );
  }, [BusinessTeams]);

  const PowerlistOptions = useMemo(() => {
    return [
      {
        id: 'all',
        name: 'All PowerLists',
      },
    ].concat(
      (powerlists ?? []).map((pl) => ({
        name: pl.name ?? '',
        id: pl.id,
      }))
    );
  }, [powerlists]);

  const QueueOptions = useMemo(() => {
    return [
      {
        queueid: 'all',
        queuename: 'All Queues',
      },
    ].concat(
      (queues ?? []).map((q) => ({
        queuename: q.queuename ?? '',
        queueid: q.queueid,
      }))
    );
  }, [queues]);

  const AgentSummary = useMemo(
    () => agentSummaryLoadable.contents?.stats ?? [],
    [agentSummaryLoadable]
  );

  const refreshHandler = getUserDirectorySubscriptionDataByUserId;

  // get initial data
  const {
    currentData: subscriptionDirectoryFetchData,
    refetch,
    isFetching,
    isLoading,
    isUninitialized,
  } = useGetUserDirectorySubscriptionDataQuery(
    {
      userSettings: {
        userId: storageUser?.contents?.userid,
        userid: storageUser?.contents?.userid,
        businessId: storageUser?.contents?.businessid,
        businessid: storageUser?.contents?.businessid,
        apikey: storageUser?.contents?.apikey,
      },
      enddate: dateRange.endDate,
      startdate: dateRange.startDate,
    },
    {
      skip:
        !dateRange.endDate ||
        !dateRange.startDate ||
        !storageUser?.contents?.businessid ||
        !storageUser?.contents?.apikey,
    }
  );
  const directorySubscriptionData = useMemo(() => {
    if (subscriptionDirectoryFetchData?.success) {
      const directorySubscriptionMap = {};
      try {
        const userDirectoryData =
          JSON.parse(
            subscriptionDirectoryFetchData?.result?.[0]?.info ?? '[]'
          ) ?? [];
        const queueSubscriptionData =
          JSON.parse(
            subscriptionDirectoryFetchData?.result?.[1]?.info ?? '[]'
          ) ?? [];
        const powerlistSubscriptionData =
          JSON.parse(
            subscriptionDirectoryFetchData?.result?.[2]?.info ?? '[]'
          ) ?? [];
        const powerlistSessionData =
          JSON.parse(
            subscriptionDirectoryFetchData?.result?.[3]?.info ?? '[]'
          ) ?? [];
        const queueCallsData =
          JSON.parse(
            subscriptionDirectoryFetchData?.result?.[4]?.info ?? '[]'
          ) ?? [];
        // parse userDirectory data
        userDirectoryData.forEach((directoryElem, index) => {
          if (directoryElem?.userid) {
            directorySubscriptionMap[directoryElem.userid] = {
              ...directoryElem,
              available:
                !directoryElem.DND &&
                !directoryElem.oncall &&
                directoryElem.loggedin
                  ? 1
                  : 0,
              id: index,
            };
          }
        });
        // parse the queue subscriptions data
        const queueSubscriptions = {};
        queueSubscriptionData.forEach((queueUser) => {
          (queueUser?.queueSubscriptions ?? []).forEach((subscription) => {
            if (!queueSubscriptions[subscription.queueId]) {
              queueSubscriptions[subscription.queueId] = {};
            }
            queueSubscriptions[subscription.queueId][queueUser.userid] = true;
          });
          if (queueUser?.userid && directorySubscriptionMap[queueUser.userid]) {
            directorySubscriptionMap[queueUser.userid] = {
              ...directorySubscriptionMap[queueUser.userid],
              queueSubscriptions: (queueUser.queueSubscriptions ?? []).sort(
                (subA, subB) => {
                  if (subA.queueId > subB.queueId) {
                    return 1;
                  }
                  if (subA.queueId < subB.queueId) {
                    return -1;
                  }
                  return 0;
                }
              ),
            };
          }
        });
        // parse the powerlist subscriptions data
        const powerlistSubscriptions = {};
        powerlistSubscriptionData.forEach((powerlistUser) => {
          (powerlistUser?.powerlistSubscriptions ?? []).forEach(
            (subscription) => {
              if (!powerlistSubscriptions[subscription.powerlistId]) {
                powerlistSubscriptions[subscription.powerlistId] = {};
              }
              powerlistSubscriptions[subscription.powerlistId][
                powerlistUser.userid
              ] = true;
            }
          );
          if (
            powerlistUser?.userid &&
            directorySubscriptionMap[powerlistUser.userid]
          ) {
            directorySubscriptionMap[powerlistUser.userid] = {
              ...directorySubscriptionMap[powerlistUser.userid],
              powerlistSubscriptions: (
                powerlistUser.powerlistSubscriptions ?? []
              ).sort((subA, subB) => {
                if (subA.powerlistPriority > subB.powerlistPriority) {
                  return -1;
                }
                if (subB.powerlistPriority > subA.powerlistPriority) {
                  return 1;
                }
                if (subB.powerlistId > subA.powerlistId) {
                  return -1;
                }
                if (subB.powerlistId < subA.powerlistId) {
                  return 1;
                }
                return 0;
              }),
            };
          }
        });
        // parse the powerlist session data
        powerlistSessionData.forEach((powerlistUser) => {
          if (
            powerlistUser?.userid &&
            directorySubscriptionMap[powerlistUser.userid]
          ) {
            directorySubscriptionMap[powerlistUser.userid] = {
              ...directorySubscriptionMap[powerlistUser.userid],
              powerlistStatus: powerlistUser.status ?? 'ended',
            };
          }
        });
        // parse the queue calls data
        queueCallsData.forEach((queueUser) => {
          if (queueUser?.userid && directorySubscriptionMap[queueUser.userid]) {
            directorySubscriptionMap[queueUser.userid] = {
              ...directorySubscriptionMap[queueUser.userid],
              queueCallsTotal: queueUser.total ?? 0,
              queueCallsAnswered: queueUser.answeredcount ?? 0,
            };
          }
        });
        const directoryArr = [];
        Object.keys(directorySubscriptionMap).forEach((userid) => {
          directoryArr.push(directorySubscriptionMap[userid]);
        });
        return {
          powerlistSubscriptions,
          queueSubscriptions,
          directory: directoryArr,
        };
      } catch (e) {
        return {
          directory: [],
          powerlistSubscriptions: {},
          queueSubscriptions: {},
        };
      }
    } else {
      return {
        directory: [],
        powerlistSubscriptions: {},
        queueSubscriptions: {},
      };
    }
  }, [subscriptionDirectoryFetchData]);
  const agentSummaryMap = useMemo(() => {
    const agentSummaryObj = {};
    (AgentSummary ?? []).forEach((agent) => {
      if (agent?.userid) {
        agentSummaryObj[agent.userid] = agent;
      }
    });
    return agentSummaryObj;
  }, [AgentSummary]);

  useEffect(() => {
    if (
      businessTeamsLoadable.state === 'hasValue' &&
      businessUsersLoadable.state === 'hasValue' &&
      agentSummaryLoadable.state === 'hasValue' &&
      directorySubscriptionData?.directory
    ) {
      refetch?.();
    }
  }, [BusinessTeams, BusinessUsers, agentSummaryMap]);

  const directoryData = useMemo(() => {
    if (
      businessTeamsLoadable.state === 'hasValue' &&
      businessUsersLoadable.state === 'hasValue' &&
      agentSummaryLoadable.state === 'hasValue' &&
      directorySubscriptionData?.directory
    ) {
      return directorySubscriptionData?.directory;
    } else {
      return [];
    }
  }, [
    directorySubscriptionData,
    BusinessTeams,
    BusinessUsers,
    agentSummaryMap,
  ]);

  useEffect(() => {
    if (directoryData?.length) {
      const liveCallUsers = directoryData.map((agent) => {
        const foundBusinessUser = BusinessUsers.find(
          (businessUser) => businessUser.userid === agent?.userid
        );

        const available = !agent.DND && !agent.oncall && agent.loggedin ? 1 : 0;
        const status = agent.oncall
          ? 'oncall'
          : agent.DND
          ? 'dnd'
          : available
          ? 'online'
          : 'offline';
        if (agent?.userid && agentSummaryMap[agent.userid]) {
          const currentDuration =
            agent.oncall && agent.callStart
              ? moment.utc(agent.callStart).local().fromNow(true)
              : '-';
          return {
            ...agent,
            ...agentSummaryMap[agent.userid],
            available,
            status,
            currentDuration,
            teams: foundBusinessUser?.teams || '',
            id: agent.userid,
          };
        } else {
          return {
            ...agent,
            available,
            status,
            currentDuration: '-',
            allcalls: 0,
            allconnections: 0,
            avgduration: '0:00:00',
            tdurationread: '0:00:00',
            teams: foundBusinessUser?.teams || '',
            id: agent.userid,
          };
        }
      });
      setDirectory(liveCallUsers);
    }
  }, [directoryData]);
  // useEffect(() => {
  //   if (filters?.length) {
  //     let nullFound = false;
  //     const filtersCopy = [...filters];
  //     if (filtersCopy[0] === null) {
  //       nullFound = true;
  //       filtersCopy[0] = {
  //         teamid: 'all',
  //         teamname: 'All Teams',
  //       };
  //     }
  //     if (filtersCopy[1] === null) {
  //       nullFound = true;
  //       filtersCopy[1] = {
  //         id: 'all',
  //         name: 'All PowerLists',
  //       };
  //     }
  //     if (filtersCopy[2] === null) {
  //       nullFound = true;
  //       filtersCopy[2] = {
  //         queueid: 'all',
  //         queuename: 'All Queues',
  //       };
  //     }
  //     if (nullFound) {
  //       setFilters(filtersCopy);
  //     }
  //   }
  // }, [filters]);

  useEffect(() => {
    const userid =
      getUserDirectorySubscriptionDataByUserIdResult?.originalArgs?.userid;
    const success =
      getUserDirectorySubscriptionDataByUserIdResult?.data?.success;
    const status = getUserDirectorySubscriptionDataByUserIdResult?.status;
    if (success && userid && status === 'fulfilled') {
      const directorySubscriptionMap = {};
      try {
        const userDirectoryData =
          JSON.parse(
            getUserDirectorySubscriptionDataByUserIdResult?.data?.result?.[0]
              ?.info
          ) ?? [];
        const queueSubscriptionData =
          JSON.parse(
            getUserDirectorySubscriptionDataByUserIdResult?.data?.result?.[1]
              ?.info
          ) ?? [];
        const powerlistSubscriptionData =
          JSON.parse(
            getUserDirectorySubscriptionDataByUserIdResult?.data?.result?.[2]
              ?.info
          ) ?? [];
        const powerlistSessionData =
          JSON.parse(
            getUserDirectorySubscriptionDataByUserIdResult?.data?.result?.[3]
              ?.info
          ) ?? [];
        const queueCallsData =
          JSON.parse(
            getUserDirectorySubscriptionDataByUserIdResult?.data?.result?.[4]
              ?.info
          ) ?? [];
        // parse userDirectory data
        userDirectoryData.forEach((directoryElem, index) => {
          if (directoryElem?.userid) {
            directorySubscriptionMap[directoryElem.userid] = {
              ...directoryElem,
              available:
                !directoryElem.DND &&
                !directoryElem.oncall &&
                directoryElem.loggedin
                  ? 1
                  : 0,
              id: index,
            };
          }
        });
        // parse the queue subscriptions data
        queueSubscriptionData.forEach((queueUser) => {
          if (queueUser?.userid && directorySubscriptionMap[queueUser.userid]) {
            directorySubscriptionMap[queueUser.userid] = {
              ...directorySubscriptionMap[queueUser.userid],
              queueSubscriptions: (queueUser.queueSubscriptions ?? []).sort(
                (subA, subB) => {
                  if (subA.queueId > subB.queueId) {
                    return 1;
                  }
                  if (subA.queueId < subB.queueId) {
                    return -1;
                  }
                  return 0;
                }
              ),
            };
          }
        });
        // parse the powerlist subscriptions data
        powerlistSubscriptionData.forEach((powerlistUser) => {
          if (
            powerlistUser?.userid &&
            directorySubscriptionMap[powerlistUser.userid]
          ) {
            directorySubscriptionMap[powerlistUser.userid] = {
              ...directorySubscriptionMap[powerlistUser.userid],
              powerlistSubscriptions: (
                powerlistUser.powerlistSubscriptions ?? []
              ).sort((subA, subB) => {
                if (subA.powerlistPriority > subB.powerlistPriority) {
                  return -1;
                }
                if (subB.powerlistPriority > subA.powerlistPriority) {
                  return 1;
                }
                if (subB.powerlistId > subA.powerlistId) {
                  return -1;
                }
                if (subB.powerlistId < subA.powerlistId) {
                  return 1;
                }
                return 0;
              }),
            };
          }
        });
        // parse the powerlist session data
        powerlistSessionData.forEach((powerlistUser) => {
          if (
            powerlistUser?.userid &&
            directorySubscriptionMap[powerlistUser.userid]
          ) {
            directorySubscriptionMap[powerlistUser.userid] = {
              ...directorySubscriptionMap[powerlistUser.userid],
              powerlistStatus: powerlistUser.status ?? 'ended',
            };
          }
        });
        // parse the queue calls data
        queueCallsData.forEach((queueUser) => {
          if (queueUser?.userid && directorySubscriptionMap[queueUser.userid]) {
            directorySubscriptionMap[queueUser.userid] = {
              ...directorySubscriptionMap[queueUser.userid],
              queueCallsTotal: queueUser.total ?? 0,
              queueCallsAnswered: queueUser.answeredcount ?? 0,
            };
          }
        });
        const directoryArr = [];
        Object.keys(directorySubscriptionMap).forEach((useridkey) => {
          directoryArr.push(directorySubscriptionMap[useridkey]);
        });
        const foundBusinessUser = BusinessUsers.find(
          (businessUser) => businessUser.userid === userid
        );
        const agent = directoryArr?.[0];
        const available =
          !agent?.DND && !agent?.oncall && agent?.loggedin ? 1 : 0;
        const status = agent?.oncall
          ? 'oncall'
          : agent?.DND
          ? 'dnd'
          : available
          ? 'online'
          : 'offline';
        if (agent?.userid && agentSummaryMap[agent.userid]) {
          const currentDuration =
            agent.oncall && agent.callStart
              ? moment.utc(agent.callStart).local().fromNow(true)
              : '-';
          directoryArr[0] = {
            ...agent,
            ...agentSummaryMap[agent.userid],
            available,
            status,
            currentDuration,
            teams: foundBusinessUser?.teams || '',
            id: agent.userid,
          };
        } else {
          directoryArr[0] = {
            ...agent,
            available,
            status,
            currentDuration: '-',
            allcalls: 0,
            allconnections: 0,
            avgduration: '0:00:00',
            tdurationread: '0:00:00',
            teams: foundBusinessUser?.teams || '',
            id: agent.userid,
          };
        }
        setDirectory(
          directory.map((elem) =>
            elem.userid === userid ? directoryArr[0] : elem
          )
        );
      } catch (e) {
        //
      }
    }
  }, [getUserDirectorySubscriptionDataByUserIdResult]);

  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshToggle((prev) => !prev);
    }, 30000);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    if (directory?.length) {
      setDirectory(
        (directory ?? []).map((directoryEntry) => {
          const currentDuration =
            directoryEntry.oncall && directoryEntry.callStart
              ? moment.utc(directoryEntry.callStart).local().fromNow(true)
              : '-';
          return {
            ...directoryEntry,
            currentDuration,
          };
        })
      );
    }
  }, [refreshToggle]);

  const filteredDirectory = useMemo(() => {
    if (!!filters?.[0]?.teamid && filters[0].teamid === 'all') {
      let i = (directory ?? []).filter((r) => r.id !== undefined);
      // powerlist filter
      if (filters?.[1]?.id && filters[1].id !== 'all') {
        i = i.filter(
          (r) =>
            directorySubscriptionData.powerlistSubscriptions?.[filters[1].id]?.[
              r.userid
            ]
        );
      }
      // queue filter
      if (filters?.[2]?.queueid && filters[2].queueid !== 'all') {
        i = i.filter(
          (r) =>
            directorySubscriptionData.queueSubscriptions[filters[2].queueid]?.[
              r.userid
            ]
        );
      }
      if (cellFieldTypeMap[orderBy] === 'string') {
        if (order === 'asc') {
          i.sort((elemA, elemB) =>
            String(elemA[orderBy] ?? '')
              .toLowerCase()
              .localeCompare(String(elemB[orderBy] ?? '').toLowerCase())
          );
        } else {
          i.sort((elemA, elemB) =>
            String(elemB[orderBy] ?? '')
              .toLowerCase()
              .localeCompare(String(elemA[orderBy] ?? '').toLowerCase())
          );
        }
      } else if (cellFieldTypeMap[orderBy] === 'duration') {
        if (order === 'asc') {
          i.sort((elemA, elemB) =>
            getDurationInSeconds(elemA[orderBy]) >
            getDurationInSeconds(elemB[orderBy])
              ? -1
              : 1
          );
        } else {
          i.sort((elemA, elemB) =>
            getDurationInSeconds(elemA[orderBy]) >
            getDurationInSeconds(elemB[orderBy])
              ? 1
              : -1
          );
        }
      } else {
        if (order === 'asc') {
          i.sort((elemA, elemB) => (elemA[orderBy] > elemB[orderBy] ? -1 : 1));
        } else {
          i.sort((elemA, elemB) => (elemA[orderBy] > elemB[orderBy] ? 1 : -1));
        }
      }

      if (statusFilter.length) {
        const statusFiltersMap = {};
        statusFilter.forEach((status) => {
          statusFiltersMap[status.status] = true;
        });
        i = i.filter((elem) => {
          if (
            (statusFiltersMap.DND && elem.DND) ||
            (statusFiltersMap.offline && !elem.loggedin) ||
            (statusFiltersMap.online && elem.loggedin) ||
            (statusFiltersMap.oncall && elem.oncall)
          ) {
            return true;
          }
          return false;
        });
      }
      return i;
    }
    let teamUsers = (directory ?? []).filter((u) => {
      if (filters?.[0] === null) {
        return true;
      }
      if (u.teams) {
        const teams = u.teams.split(',');
        const i = teams.findIndex((t) => parseInt(t) === filters?.[0]?.teamid);
        if (i > -1) {
          return true;
        }
      }
      return false;
    });
    // powerlist filter
    if (filters?.[1]?.id && filters[1].id !== 'all') {
      teamUsers = teamUsers.filter(
        (r) =>
          directorySubscriptionData.powerlistSubscriptions?.[filters[1].id]?.[
            r.userid
          ]
      );
    }
    // queue filter
    if (filters?.[2]?.queueid && filters[2].queueid !== 'all') {
      teamUsers = teamUsers.filter(
        (r) =>
          directorySubscriptionData.queueSubscriptions?.[filters[2].queueid]?.[
            r.userid
          ]
      );
    }
    if (cellFieldTypeMap[orderBy] === 'string') {
      if (order === 'asc') {
        teamUsers.sort((elemA, elemB) =>
          String(elemA[orderBy] ?? '')
            .toLowerCase()
            .localeCompare(String(elemB[orderBy] ?? '').toLowerCase())
        );
      } else {
        teamUsers.sort((elemA, elemB) =>
          String(elemB[orderBy] ?? '')
            .toLowerCase()
            .localeCompare(String(elemA[orderBy] ?? '').toLowerCase())
        );
      }
    } else if (cellFieldTypeMap[orderBy] === 'duration') {
      if (order === 'asc') {
        teamUsers.sort((elemA, elemB) =>
          getDurationInSeconds(elemA[orderBy]) >
          getDurationInSeconds(elemB[orderBy])
            ? -1
            : 1
        );
      } else {
        teamUsers.sort((elemA, elemB) =>
          getDurationInSeconds(elemA[orderBy]) >
          getDurationInSeconds(elemB[orderBy])
            ? 1
            : -1
        );
      }
    } else {
      if (order === 'asc') {
        teamUsers.sort((elemA, elemB) =>
          elemA[orderBy] > elemB[orderBy] ? -1 : 1
        );
      } else {
        teamUsers.sort((elemA, elemB) =>
          elemA[orderBy] > elemB[orderBy] ? 1 : -1
        );
      }
    }
    const statusFiltersMap = {};
    if (statusFilter.length) {
      statusFilter.forEach((status) => {
        statusFiltersMap[status.status] = true;
      });
      teamUsers = teamUsers.filter((elem) => {
        if (
          (statusFiltersMap.DND && elem.DND) ||
          (statusFiltersMap.loggedin && !elem.loggedin) ||
          (statusFiltersMap.loggedin && elem.loggedin) ||
          (statusFiltersMap.oncall && elem.oncall)
        ) {
          return true;
        }
        return false;
      });
    }
    return teamUsers;
  }, [directory, filters, order, orderBy, statusFilter]);

  useEffect(() => {
    const expandMap = {};
    (directory ?? []).forEach((directoryElem) => {
      expandMap[directoryElem.userid] = openArr[directoryElem.userid] ?? false;
    });
    setOpenArr(expandMap);
    setThrottleArr(expandMap);
  }, [directory?.length]);

  const handleTeam = (e, val) => {
    const filtersCopy = [...filters];
    filtersCopy[0] = val;
    setFilters(filtersCopy);
  };

  const handlePowerlist = (e, val) => {
    const filtersCopy = [...filters];
    filtersCopy[1] = val;
    setFilters(filtersCopy);
  };

  const handleQueue = (e, val) => {
    const filtersCopy = [...filters];
    filtersCopy[2] = val;
    setFilters(filtersCopy);
  };

  const handleStatus = (e, val) => {
    // const filtersCopy = [...filters];
    // filtersCopy[3] = val;
    // setFilters(filtersCopy);
    // console.log('val', val);
    setStatusFilter(val);
  };

  const subscribeDirectoryEvent = {
    target: 'powercall',
    group: 'directory',
    businessId: storageUser.contents?.businessid,
  };

  // sync with apollo graphql fetch
  useSubscription(getOnCreateEventQuery(subscribeDirectoryEvent), {
    onData: ({ data }) => {
      // deserialize gql message payload
      const gqlMessage =
        data.data?.onCreateEvent.payload?.startsWith('{') &&
        data.data?.onCreateEvent.payload?.endsWith('}') &&
        JSON.parse(data.data?.onCreateEvent.payload);
      // replace with updated directory entry
      if (gqlMessage && 'directory' in gqlMessage.message) {
        if (gqlMessage.message.messageType !== 'queueDirectoryUpdate') {
          const directoryUser = gqlMessage.message.directory;
          const userid = gqlMessage.message.directory.userid;
          const foundBusinessUser = BusinessUsers.find(
            (businessUser) => businessUser.userid === userid
          );

          const available =
            !directoryUser.DND &&
            !directoryUser.oncall &&
            directoryUser.loggedin
              ? 1
              : 0;
          const status = directoryUser.oncall
            ? 'oncall'
            : directoryUser.DND
            ? 'dnd'
            : available
            ? 'online'
            : 'offline';
          let directoryUpdate;
          if (gqlMessage.message.directory.newuser) {
            // update state atomically
            directoryUpdate = {
              ...directoryUser,
              available,
              status,
              currentDuration: '-',
              allcalls: 0,
              allconnections: 0,
              avgduration: '0:00:00',
              tdurationread: '0:00:00',
              teams: foundBusinessUser?.teams || '',
              id: userid,
            };
            setDirectory((state) => [...state, directoryUpdate]);
          } else {
            // update state atomically
            if (userid && agentSummaryMap[userid]) {
              const currentDuration =
                directoryUser?.oncall && directoryUser.callStart
                  ? moment.utc(directoryUser.callStart).local().fromNow(true)
                  : '-';
              directoryUpdate = {
                ...directoryUser,
                ...agentSummaryMap[userid],
                available,
                status,
                currentDuration,
                teams: foundBusinessUser?.teams || '',
                id: userid,
              };
            } else {
              directoryUpdate = {
                ...directoryUser,
                available,
                status,
                currentDuration: '-',
                allcalls: 0,
                allconnections: 0,
                avgduration: '0:00:00',
                tdurationread: '0:00:00',
                teams: foundBusinessUser?.teams || '',
                id: userid,
              };
            }
            setDirectory((state) =>
              state.map((entry) =>
                'directory' in gqlMessage.message &&
                entry.userid === gqlMessage.message.directory.userid
                  ? {
                      ...entry,
                      ...directoryUpdate,
                      customerName: directoryUser.oncall
                        ? directoryUpdate.customerName
                          ? directoryUpdate.customerName
                          : entry.customerName
                        : '',
                      crmlink: directoryUser.oncall
                        ? directoryUpdate.crmlink
                          ? directoryUpdate.crmlink
                          : entry.crmlink
                        : null,
                    }
                  : entry
              )
            );
          }
          // console.log(
          //   'AgentDirectory.tsx: onSubscriptionDirectoryData',
          //   gqlMessage,
          // );
        }
      }
    },
    variables: subscribeDirectoryEvent,
  });

  const slideComponentProps = {
    classes,
    refreshHandler,
    userSettings: {
      userId: storageUser?.contents?.userid,
      userid: storageUser?.contents?.userid,
      businessId: storageUser?.contents?.businessid,
      businessid: storageUser?.contents?.businessid,
      apikey: storageUser?.contents?.apikey,
      role: storageUser?.contents?.user?.role ?? 'user',
    },
    enddate: dateRange.endDate,
    startdate: dateRange.startDate,
    throttleArr,
    setThrottleArr,
    setEditEntity,
  };

  return (
    <Paper className={classes.paper}>
      <div
        style={{
          width: 'auto',
        }}
      >
        <div className={classes.dropdownContainer}>
          <MaterialAutocomplete
            type="text"
            className={classes.dropdown}
            label="Teams"
            size="small"
            variant="outlined"
            multiple={false}
            options={BusinessTeamsOptions}
            value={filters[0]}
            onChange={handleTeam}
            getOptionLabel={(option) => (option ? `${option?.teamname}` : '')}
            isOptionEqualToValue={(option, value) =>
              option?.teamid === value?.teamid
            }
          />
          <MaterialAutocomplete
            type="text"
            className={classes.dropdown}
            label="PowerLists"
            size="small"
            variant="outlined"
            multiple={false}
            options={PowerlistOptions}
            value={filters[1]}
            onChange={handlePowerlist}
            getOptionLabel={(option) => (option ? `${option?.name}` : '')}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
          />
          <MaterialAutocomplete
            type="text"
            className={classes.dropdown}
            label="Queues"
            size="small"
            variant="outlined"
            multiple={false}
            options={QueueOptions}
            value={filters[2]}
            onChange={handleQueue}
            getOptionLabel={(option) => (option ? `${option?.queuename}` : '')}
            isOptionEqualToValue={(option, value) =>
              option?.queueid === value?.queueid
            }
          />
          <MaterialAutocomplete
            type="text"
            className={classes.dropdown}
            label="Status"
            size="small"
            variant="outlined"
            multiple={true}
            options={[
              {
                status: 'offline',
                title: 'Offline',
                color: KIXIE_COLORS.loggedOut,
              },
              {
                status: 'online',
                title: 'Online',
                color: KIXIE_COLORS.available,
              },
              {
                status: 'oncall',
                title: 'On Call',
                color: KIXIE_COLORS.onCall,
              },
              {
                status: 'DND',
                title: 'Do-Not-Disturb (DND)',
                color: KIXIE_COLORS.dnd,
              },
            ]}
            value={statusFilter}
            onChange={handleStatus}
            getOptionLabel={(option) => (option ? `${option?.title}` : '')}
            renderOption={(props, option, { selected }) => {
              return (
                <li {...props}>
                  <>
                    <Checkbox
                      color="primary"
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    <AlbumIcon
                      style={{ color: option.color, marginRight: 5 }}
                    />
                    {option.title}
                  </>
                </li>
              );
            }}
            isOptionEqualToValue={(option, value) =>
              option?.status === value?.status
            }
          />
          <div onClick={resetFilters} className={classes.clearButton}>
            clear
          </div>
        </div>
        <DataTable
          rows={filteredDirectory}
          expandable={true}
          columns={headCells}
          order={order}
          orderBy={orderBy}
          setOrder={setOrder}
          setOrderBy={setOrderBy}
          toggleExpandAll={toggleExpandAll}
          allOpen={allOpen}
          openMap={openArr}
          slideComponent={CallBoardAccordionRow}
          slideComponentProps={slideComponentProps}
          loading={isFetching || isLoading || isUninitialized}
        />
      </div>
      <QueueEditDialog
        show={editEntity.type === 'queue' && !!editEntity.id}
        id={String(editEntity.id ?? 0)}
        onHide={() => {
          setEditEntity({
            type: null,
            id: null,
          });
        }}
      />
      <PowerlistModal
        show={editEntity.type === 'powerlist' && !!editEntity.id}
        id={editEntity.id}
        handleClose={() => {
          setEditEntity({
            type: null,
            id: null,
          });
        }}
        enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
        powerlistReportCache={{
          current: {
            powerlists,
          },
        }}
      />
    </Paper>
  );
}

const powerlistUserSessionStatusMap = {
  started: {
    label: 'In Session',
    class: 'started',
    text: 'startedText',
    icon: 'startedIcon',
    component: PauseIcon,
  },
  ended: {
    label: 'Not In Session',
    class: 'ended',
    text: 'endedText',
    icon: 'endedIcon',
    component: PlayArrowIcon,
  },
  onPause: {
    label: 'Session Paused',
    class: 'onPause',
    text: 'onPauseText',
    icon: 'onPauseIcon',
    component: PlayArrowIcon,
  },
  onStandby: {
    label: 'On Standby',
    class: 'started',
    text: 'startedText',
    icon: 'startedIcon',
    component: PauseIcon,
  },
};

function CallBoardAccordionRow(props) {
  const powerlistStatus = props.row?.powerlistStatus
    ? props.row.powerlistStatus
    : 'ended';
  // const queueCallsCount = props.row.queueCallsTotal ?? 0;
  // const answeredQueueCalls = props.row.queueCallsAnswered ?? 0;
  // const answeredQueueCallsLength = answeredQueueCalls
  //   ? 100 * (answeredQueueCalls / queueCallsCount)
  //   : 2;
  // const missedQueueCalls = queueCallsCount - answeredQueueCalls;
  // const missedQueueCallsLength = missedQueueCalls
  //   ? 100 * (missedQueueCalls / queueCallsCount)
  //   : 2;

  const canManage =
    props.data?.userSettings?.role === 'superadmin' ||
    props.data?.userSettings?.role === 'admin' ||
    props.data?.userSettings?.role === 'manager';

  const throttledRefreshHandler = () => {
    if (props.data?.throttleArr?.[props.row?.userid] === false) {
      props.data.refreshHandler({
        userSettings: {
          userId: props.row.userid,
          userid: props.row.userid,
          businessId: props.data.userSettings.businessid,
          businessid: props.data.userSettings.businessid,
          apikey: props.data.userSettings.apikey,
        },
        userid: props.row.userid,
        enddate: props.data.enddate,
        startdate: props.data.startdate,
      });
      props.data.setThrottleArr({
        ...props.data.throttleArr,
        [props.row.userid]: true,
      });
    }
    setTimeout(() => {
      props.data.setThrottleArr({
        ...props.data.throttleArr,
        [props.row.userid]: false,
      });
    }, 5000);
  };

  return (
    <div className={props.data.classes.accordionContainer}>
      <div className={props.data.classes.powerlistContainer}>
        <div className={props.data.classes.refreshButtonContainer}>
          <Tooltip title="Refresh">
            <IconButton
              onClick={throttledRefreshHandler}
              className={props.data.classes.refreshButton}
              size="large"
            >
              <RefreshIcon className={props.data.classes.refreshIcon} />
            </IconButton>
          </Tooltip>
        </div>
        <div className={props.data.classes.powerlistListContainer}>
          <div className={props.data.classes.accordionHeaderContainer}>
            <BallotIcon className={props.data.classes.accordionHeaderIcon} />
            <div className={props.data.classes.accordionHeader}>PowerLists</div>
          </div>
          <div className={props.data.classes.powerlistContentContainer}>
            {(props.row?.powerlistSubscriptions ?? []).map(
              (powerlistSubscription, index) => {
                return (
                  <div
                    className={props.data.classes.powerlistCheckboxContainer}
                    key={index}
                  >
                    <Checkbox
                      className={props.data.classes.subscriptionCheckbox}
                      color="primary"
                      checked={!!powerlistSubscription.isSubscribed}
                    />
                    <p
                      onClick={() => {
                        if (canManage) {
                          props.data?.setEditEntity?.({
                            type: 'powerlist',
                            id: powerlistSubscription.powerlistId,
                          });
                        }
                      }}
                      className={
                        canManage
                          ? props.data.classes.subscriptionLink
                          : props.data.classes.subscriptionText
                      }
                    >
                      {powerlistSubscription.name ?? ''}
                    </p>
                  </div>
                );
              }
            )}
          </div>
        </div>
        <div className={props.data.classes.powerlistSessionStatusContainer}>
          <div className={props.data.classes.accordionHeaderContainer}>
            <div className={props.data.classes.accordionHeader}>
              Session Status
            </div>
          </div>
          <div className={props.data.classes.powerlistContentContainer2}>
            <div
              className={
                props.data.classes[
                  powerlistUserSessionStatusMap?.[powerlistStatus]?.class
                ] ?? null
              }
            >
              <div
                className={
                  props.data.classes[
                    powerlistUserSessionStatusMap?.[powerlistStatus]?.text ??
                      null
                  ]
                }
              >
                {powerlistUserSessionStatusMap?.[powerlistStatus]?.label ?? ''}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={props.data.classes.verticalRuler} />
      <div className={props.data.classes.queueContainer}>
        <div className={props.data.classes.queuesListContainer}>
          <div className={props.data.classes.accordionHeaderContainer}>
            <QueueIcon className={props.data.classes.accordionHeaderIcon} />
            <div className={props.data.classes.accordionHeader}>Queues</div>
          </div>
          <div className={props.data.classes.powerlistContentContainer}>
            {(props.row?.queueSubscriptions ?? []).map(
              (queueSubscription, index) => {
                return (
                  <div
                    className={props.data.classes.powerlistCheckboxContainer}
                    key={index}
                  >
                    <Checkbox
                      className={props.data.classes.subscriptionCheckbox}
                      color="primary"
                      checked={!!queueSubscription.isSubscribed}
                    />
                    <p
                      onClick={() => {
                        if (canManage) {
                          props.data?.setEditEntity?.({
                            type: 'queue',
                            id: queueSubscription.queueId,
                          });
                        }
                      }}
                      className={
                        canManage
                          ? props.data.classes.subscriptionLink
                          : props.data.classes.subscriptionText
                      }
                    >
                      {queueSubscription.name ?? ''}
                    </p>
                  </div>
                );
              }
            )}
          </div>
        </div>
        {/* <div className={props.data.classes.statsContainer}>
          <div className={props.data.classes.queueCallsContainer}>
            <div className={props.data.classes.callsHandledContainer}>
              <div className={props.data.classes.accordionHeaderContainer}>
                <div className={props.data.classes.accordionHeader}>
                  Calls Handled
                </div>
              </div>
              <div className={props.data.classes.powerlistContentContainer2}>
                <div>
                  <div>{answeredQueueCalls}</div>
                </div>
              </div>
            </div>
            <div className={props.data.classes.callsMissedContainer}>
              <div className={props.data.classes.accordionHeaderContainer}>
                <div className={props.data.classes.accordionHeader}>
                  Calls Missed
                </div>
              </div>
              <div className={props.data.classes.powerlistContentContainer2}>
                <div>
                  <div>{missedQueueCalls}</div>
                </div>
              </div>
            </div>
          </div>
          {queueCallsCount ? (
            <div className={props.data.classes.queueCallsBarContainer}>
              <div
                style={{ width: `${answeredQueueCallsLength}%` }}
                className={props.data.classes.queueCallsContainerAnswered}
              />
              <div className={props.data.classes.queueCallsBarDivider} />
              <div
                style={{ width: `${missedQueueCallsLength}%` }}
                className={props.data.classes.queueCallsContainerMissed}
              />
            </div>
          ) : (
            <div className={props.data.classes.queueCallsBarContainer}>
              <div className={props.data.classes.queueCallsContainerNone} />
            </div>
          )}
        </div> */}
      </div>
    </div>
  );
}
